/**
 * TaskCommentsSection — the task's internal notes: list + create +
 * author edit/delete (T2.3.4 / T2.4.2, relabelled in TASK-NOTES1).
 *
 * Mounted inside the ``<Section title="Notizen">`` slot on
 * AdminTaskDetailPage. The USER-FACING word is "Notiz"; the file, the
 * table (``task_comments``) and the API keep their names, because
 * renaming a shipped table and four endpoints to change a caption would
 * be a migration and a wire break for a wording decision.
 *
 * What belongs here, versus its neighbours on the page: the description
 * says what has to be done, the activity feed records what the SYSTEM
 * did, and this is where people write to each other while the task
 * runs. Self-contained: fetches its own data, owns its
 * own loading/error states, and recovers from comment-endpoint 404 via
 * an inline [Erneut versuchen] button rather than redirecting (the
 * parent page already handles task-level 404).
 *
 * T2.4.2 additions:
 *   - ``currentUserId`` prop: which user is logged in (from ``useAuth``
 *     in the parent). Edit + delete buttons render only when
 *     ``comment.user_id === currentUserId``. Backend also enforces
 *     author-only at the route layer — frontend gating is UX, not security.
 *   - ``onCommentChanged`` prop (replaces ``onCommentAdded``): fires
 *     after ANY successful mutation (add / edit / delete) so sibling
 *     sections — currently only the activity feed — can refetch.
 *
 * Inline edit (Linear/GitHub-style): only one comment can be in edit
 * mode at a time. Starting a second edit while another row is dirty
 * prompts ``window.confirm("Aktuelle Bearbeitung verwerfen?")``.
 *
 * Delete UX is conservative — the row stays visible until the server
 * returns 204. On failure the row stays + a toast surfaces the error.
 * No optimistic removal: refetch is the source of truth, never local
 * filter on the stale array.
 *
 * Author display fallback chain (STRICT — no UUID exposure anywhere):
 *   user_full_name → user_email → "Unbekannt"
 *
 * Timestamps render via the central `formatDateTime` helper
 * (Zurich-CEST wall-clock).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  createAdminTaskComment,
  deleteAdminTaskComment,
  fetchAdminTaskComments,
  updateAdminTaskComment,
} from "../../../api/adminData";
import { formatDateTime } from "../../../utils/datetime";


// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

const MAX_BODY = 5000;
const CHAR_COUNT_THRESHOLD = 4500; // show counter once user is close to the limit

/** STRICT fallback chain — never expose a raw UUID. */
function authorDisplay(c) {
  if (c?.user_full_name && c.user_full_name.trim()) return c.user_full_name.trim();
  if (c?.user_email && c.user_email.trim()) return c.user_email.trim();
  return "Unbekannt";
}


// ──────────────────────────────────────────────────────────────────────
// Comment row (handles both read mode and inline edit mode)
// ──────────────────────────────────────────────────────────────────────

function CommentRow({
  comment,
  isLast,
  isAuthor,
  isEditing,
  isDeleting,
  editingBody,
  editSubmitting,
  canSaveEdit,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteRequest,
  onEditingBodyChange,
}) {
  const showActions = isAuthor && !isEditing && !isDeleting;
  const wasEdited = !!comment.updated_at;
  // S6 theme fix: semantic tokens carry light mode; dark: pins keep
  // the previous dark appearance byte-for-byte (see AdminTaskDetailPage).
  const fieldInput =
    "block w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0] dark:focus:border-[#3a3f55]";
  const subtleBtn =
    "min-h-[28px] rounded-[6px] border border-transparent bg-transparent px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:text-[#94a3b8] dark:hover:bg-[#13161f] dark:hover:text-[#c5cbe0]";

  return (
    <article
      className={
        "py-3 " +
        (isLast ? "" : "border-b border-border/60 dark:border-[#1e2130]/60")
      }
    >
      <header className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 text-[12px]">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-semibold text-foreground dark:text-[#c5cbe0]">
            {authorDisplay(comment)}
          </span>
          <span
            aria-hidden="true"
            className="text-muted-foreground dark:text-[#4a5070]"
          >
            •
          </span>
          <time
            className="tabular-nums text-muted-foreground dark:text-[#4a5070]"
            dateTime={comment.created_at || undefined}
          >
            {formatDateTime(comment.created_at)}
          </time>
          {wasEdited ? (
            <span
              className="text-[11px] italic text-muted-foreground dark:text-[#4a5070]"
              title={`Bearbeitet: ${formatDateTime(comment.updated_at)}`}
            >
              (bearbeitet)
            </span>
          ) : null}
        </div>
        {showActions ? (
          <div className="flex items-center gap-1">
            {/* S6 a11y: timestamp context makes each name unique — the
                bare visible "Bearbeiten" collides with the page-level
                task [Bearbeiten] button for screen-reader users. */}
            <button
              type="button"
              onClick={() => onStartEdit(comment)}
              aria-label={`Notiz vom ${formatDateTime(comment.created_at)} bearbeiten`}
              className={subtleBtn}
            >
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={() => onDeleteRequest(comment)}
              aria-label={`Notiz vom ${formatDateTime(comment.created_at)} löschen`}
              className={subtleBtn}
            >
              Löschen
            </button>
          </div>
        ) : null}
      </header>

      {isEditing ? (
        <div className="grid gap-2">
          <textarea
            value={editingBody}
            onChange={(e) => onEditingBodyChange(e.target.value)}
            rows={3}
            maxLength={MAX_BODY + 1}
            disabled={editSubmitting}
            className={fieldInput}
            aria-label="Notiz bearbeiten"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={editSubmitting}
              className="min-h-[36px] rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={onSaveEdit}
              disabled={!canSaveEdit}
              className="min-h-[36px] rounded-[8px] bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
            >
              {editSubmitting ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </div>
      ) : (
        <p
          className={
            "m-0 whitespace-pre-wrap text-[13px] " +
            (isDeleting
              ? "text-muted-foreground opacity-50 dark:text-[#4a5070]"
              : "text-foreground dark:text-[#c5cbe0]")
          }
        >
          {comment.body}
        </p>
      )}
    </article>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────

export default function TaskCommentsSection({
  taskId,
  currentUserId,
  onCommentChanged,
  /** REPORT-NOTES1: the note ENGINE is entity-agnostic — an ``api``
   * override ({list, create(body), update(id, body), remove(id)})
   * rebinds it to another parent (WorkReportCommentsSection does).
   * Without it the task API applies, byte-for-byte as before. */
  api = null,
  /** Frozen parent (e.g. a cancelled report): notes stay readable,
   * every mutation surface disappears and ``readOnlyHint`` explains
   * why. The backend enforces the same freeze with 409s — this is UX,
   * not security (same stance as the author-only gating). */
  readOnly = false,
  readOnlyHint = null,
  /** Unique textarea id per page — two note sections on one page must
   * not collide on label/id. */
  textareaId = "task-comment-body",
}) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { message, status }

  // Create-form state
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inline-edit state (lifted here so only one row can edit at a time)
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete-in-flight marker (dims the row's body while DELETE is pending)
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const listFn = api?.list;
  const load = useCallback(() => {
    if (!taskId) return Promise.resolve();
    setLoading(true);
    setLoadError(null);
    return (listFn ? listFn() : fetchAdminTaskComments(taskId))
      .then((data) => {
        setComments(Array.isArray(data?.comments) ? data.comments : []);
      })
      .catch((err) => {
        const status = err?.status;
        const message =
          err?.message || "Notizen konnten nicht geladen werden.";
        setLoadError({ message, status });
        if (status !== 404) {
          toast.error(message);
        }
      })
      .finally(() => setLoading(false));
  }, [taskId, listFn]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Create form derived state ─────────────────────────────────────
  const trimmed = body.trim();
  const tooLong = body.length > MAX_BODY;
  const canSubmit = trimmed.length >= 1 && !tooLong && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await (api?.create
        ? api.create(trimmed)
        : createAdminTaskComment(taskId, trimmed));
      await load();
      setBody("");
      toast.success("Notiz hinzugefügt.");
      if (typeof onCommentChanged === "function") {
        // Signal the parent so sibling sections (activity feed) refetch.
        onCommentChanged();
      }
    } catch (err) {
      toast.error(err?.message || "Notiz konnte nicht gespeichert werden.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit-mode derived state + handlers ───────────────────────────
  const editingOriginal = editingCommentId
    ? comments.find((c) => c.id === editingCommentId)
    : null;
  const editTrimmed = editingBody.trim();
  const editTooLong = editingBody.length > MAX_BODY;
  const editChanged =
    editingOriginal != null &&
    editTrimmed !== (editingOriginal.body || "").trim();
  const canSaveEdit =
    editTrimmed.length >= 1 &&
    !editTooLong &&
    !editSubmitting &&
    editChanged;

  function handleStartEdit(comment) {
    if (
      editingCommentId &&
      editingCommentId !== comment.id &&
      editingOriginal
    ) {
      const otherDirty =
        editingBody.trim() !== (editingOriginal.body || "").trim();
      if (
        otherDirty &&
        !window.confirm("Aktuelle Bearbeitung verwerfen?")
      ) {
        return;
      }
    }
    setEditingCommentId(comment.id);
    setEditingBody(comment.body || "");
  }

  function handleCancelEdit() {
    setEditingCommentId(null);
    setEditingBody("");
  }

  async function handleSaveEdit() {
    if (!editingCommentId || !editingOriginal) return;
    if (!canSaveEdit) return;
    setEditSubmitting(true);
    try {
      await (api?.update
        ? api.update(editingCommentId, editingBody)
        : updateAdminTaskComment(taskId, editingCommentId, editingBody));
      await load();
      setEditingCommentId(null);
      setEditingBody("");
      toast.success("Notiz aktualisiert.");
      if (typeof onCommentChanged === "function") onCommentChanged();
    } catch (err) {
      toast.error(
        err?.message || "Notiz konnte nicht aktualisiert werden."
      );
      // Edit mode stays active so the user can fix + retry.
    } finally {
      setEditSubmitting(false);
    }
  }

  // ── Delete handler ────────────────────────────────────────────────
  async function handleDeleteRequest(comment) {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    setDeletingCommentId(comment.id);
    try {
      await (api?.remove
        ? api.remove(comment.id)
        : deleteAdminTaskComment(taskId, comment.id));
      // Refetch is the source of truth — never optimistically filter.
      await load();
      toast.success("Notiz gelöscht.");
      if (typeof onCommentChanged === "function") onCommentChanged();
    } catch (err) {
      toast.error(err?.message || "Notiz konnte nicht gelöscht werden.");
      // Row stays visible; clearing the marker re-enables its buttons.
    } finally {
      setDeletingCommentId(null);
    }
  }

  // ── Loading / error states ────────────────────────────────────────
  if (loading && comments.length === 0 && !loadError) {
    return (
      <p
        role="status"
        className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]"
      >
        Lade Notizen …
      </p>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="rounded-[8px] border border-dashed border-destructive/40 bg-destructive/10 p-4 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
      >
        <p className="m-0 text-[13px] text-destructive dark:text-[#ff6b6b]">
          Aufgabe oder Notizen konnten nicht geladen werden.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-3 min-h-[40px] rounded-[8px] border border-border bg-transparent px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  // ── Normal render ─────────────────────────────────────────────────
  return (
    <div className="grid gap-3">
      {readOnly ? (
        readOnlyHint ? (
          <p className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]">
            {readOnlyHint}
          </p>
        ) : null
      ) : (
      <form
        onSubmit={handleSubmit}
        className="grid gap-2"
      >
        <label htmlFor={textareaId} className="sr-only">
          Notiz
        </label>
        <textarea
          id={textareaId}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={MAX_BODY + 1} /* +1 so the validator catches overflow */
          placeholder="Interne Notiz …"
          disabled={submitting}
          className="block w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0] dark:focus:border-[#3a3f55]"
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          {body.length > CHAR_COUNT_THRESHOLD ? (
            <span
              className={
                "text-[11px] tabular-nums " +
                (tooLong
                  ? "text-destructive dark:text-[#ff6b6b]"
                  : "text-muted-foreground dark:text-[#94a3b8]")
              }
            >
              {body.length} / {MAX_BODY}
            </span>
          ) : (
            <span aria-hidden />
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-[44px] w-full rounded-[8px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 sm:w-auto dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
          >
            {submitting ? "Sendet …" : "Senden"}
          </button>
        </div>
      </form>
      )}

      {/* NOTES-UX1: the list sits BELOW the composer, and carries the
          divider the composer used to own — the rule belongs between
          the two, and the two swapped places. */}
      {comments.length === 0 ? (
        <p className="m-0 border-t border-border pt-3 text-[12px] text-muted-foreground dark:border-[#1e2130] dark:text-[#4a5070]">
          Noch keine Notizen.
        </p>
      ) : (
        <div className="border-t border-border pt-1 dark:border-[#1e2130]">
          {comments.map((c, i) => (
            <CommentRow
              key={c.id}
              comment={c}
              isLast={i === comments.length - 1}
              isAuthor={
                !readOnly && !!currentUserId && c.user_id === currentUserId
              }
              isEditing={editingCommentId === c.id}
              isDeleting={deletingCommentId === c.id}
              editingBody={editingBody}
              editSubmitting={editSubmitting}
              canSaveEdit={canSaveEdit}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onDeleteRequest={handleDeleteRequest}
              onEditingBodyChange={setEditingBody}
            />
          ))}
        </div>
      )}
    </div>
  );
}
